import {
  type AIProviderId,
  availableManagedChatModels,
  type ManagedChatModel,
  resolveManagedAIProviderId,
} from "../shared/aiProviders";
import {
  missingRequiredExternalApiUrl,
  missingRequiredExternalPublicUrl,
  missingRequiredExternalUiUrl,
} from "../shared/stackMode";

export type Favicon = "logo-red.svg" | "logo-yellow.svg" | "logo-purple.svg";

type EnvironmentConfig = {
  externalApiUrl?: string;
  externalUiUrl?: string;
  externalPublicUrl?: string;
  stackBaseDomain: string;
  stackPublicBaseDomain?: string;
  publicWildcardDomain?: string;
  inboundEmailDomain?: string;
  zeroSyncUrl: string;
  managedAIProvider?: AIProviderId;
  managedChatModels: readonly ManagedChatModel[];
  // Deployment-level force-off for the managed provider; tenant admins can't
  // change it. The admin-controlled toggle lives on the tenants row instead.
  managedAIProviderDisabled: boolean;
  // Deployment-level force-on for the route-auth kill switch; tenant admins
  // can't turn it off. Enforcement lives in services/public; this only locks
  // the settings toggles so the UI isn't misleading.
  routeAuthForceDisabled: boolean;
  gitSha: string;
  title: string;
  favicon?: Favicon;
  sentryDsn?: string;
  intercomAppId?: string;
  stackName?: string;
  hasOAuthClients: boolean;
  managedOAuthClientIds: Record<string, string>;
  tracingEnabled: boolean;
  tunnelEnabled: boolean;
  showFeatureFlagsNavDefault: boolean;
  selfHostedSingleTenant: boolean;
  legacyApiSubdomain: boolean;
  combinedSpaceTenantHosts: boolean;
};

const template = document.getElementById("ui-environment");

if (!(template instanceof HTMLTemplateElement)) {
  throw new Error(
    'Missing <template id="ui-environment"> for UI environment config'
  );
}

const json = template.content.textContent?.trim();

if (!json) {
  throw new Error(
    'Empty <template id="ui-environment"> for UI environment config'
  );
}

const parsed = JSON.parse(json) as Omit<
  EnvironmentConfig,
  | "hasOAuthClients"
  | "managedOAuthClientIds"
  | "tracingEnabled"
  | "tunnelEnabled"
  | "showFeatureFlagsNavDefault"
  | "selfHostedSingleTenant"
  | "managedAIProviderDisabled"
  | "routeAuthForceDisabled"
  | "managedChatModels"
  | "legacyApiSubdomain"
  | "combinedSpaceTenantHosts"
> & {
  favicon?: string;
  sentryDsn?: string;
  intercomAppId?: string;
  anthropicBedrockRegion?: string;
  hasAnthropicApiKey?: string;
  hasOpenAIApiKey?: string;
  managedAIProviderDisabled?: string;
  routeAuthForceDisabled?: string;
  hasOAuthClients?: string;
  managedOAuthClientIds?: Record<string, string>;
  tracingEnabled?: string;
  tunnelEnabled?: string;
  showFeatureFlagsNavDefault?: string;
  selfHostedSingleTenant?: string;
  legacyApiSubdomain?: string;
  combinedSpaceTenantHosts?: string;
};

function optionalEnvironmentValue(value?: string) {
  return value && !/^\$\{.+\}$/.test(value) ? value : undefined;
}

const {
  anthropicBedrockRegion,
  hasAnthropicApiKey,
  hasOpenAIApiKey,
  ...parsedEnvironment
} = parsed;
const aiProviderEnvironment = {
  anthropicBedrockRegion: optionalEnvironmentValue(anthropicBedrockRegion),
  anthropicApiKeyConfigured: hasAnthropicApiKey === "true",
  openAIApiKeyConfigured: hasOpenAIApiKey === "true",
};

export const config: EnvironmentConfig = {
  ...parsedEnvironment,
  externalApiUrl: optionalEnvironmentValue(parsed.externalApiUrl),
  externalUiUrl: optionalEnvironmentValue(parsed.externalUiUrl),
  externalPublicUrl: optionalEnvironmentValue(parsed.externalPublicUrl),
  stackBaseDomain: optionalEnvironmentValue(parsed.stackBaseDomain) ?? "",
  stackPublicBaseDomain: optionalEnvironmentValue(parsed.stackPublicBaseDomain),
  favicon: parsed.favicon || undefined,
  sentryDsn: parsed.sentryDsn || undefined,
  intercomAppId: optionalEnvironmentValue(parsed.intercomAppId),
  managedAIProvider:
    resolveManagedAIProviderId(aiProviderEnvironment) ?? undefined,
  managedChatModels: availableManagedChatModels(aiProviderEnvironment),
  managedAIProviderDisabled: parsed.managedAIProviderDisabled === "true",
  routeAuthForceDisabled: parsed.routeAuthForceDisabled === "true",
  stackName: parsed.stackName || undefined,
  hasOAuthClients: parsed.hasOAuthClients === "true",
  managedOAuthClientIds: parsed.managedOAuthClientIds ?? {},
  tracingEnabled: parsed.tracingEnabled === "true",
  tunnelEnabled: parsed.tunnelEnabled === "true",
  showFeatureFlagsNavDefault: parsed.showFeatureFlagsNavDefault === "true",
  selfHostedSingleTenant: parsed.selfHostedSingleTenant === "true",
  legacyApiSubdomain: parsed.legacyApiSubdomain === "true",
  combinedSpaceTenantHosts: parsed.combinedSpaceTenantHosts === "true",
};

if (
  missingRequiredExternalUiUrl({
    stackBaseDomain: config.stackBaseDomain,
    selfHostedSingleTenant: config.selfHostedSingleTenant,
    externalUiUrl: config.externalUiUrl,
  })
) {
  throw new Error("Missing required environment variable: EXTERNAL_UI_URL");
}
if (
  missingRequiredExternalApiUrl({
    stackBaseDomain: config.stackBaseDomain,
    selfHostedSingleTenant: config.selfHostedSingleTenant,
    externalApiUrl: config.externalApiUrl,
  })
) {
  throw new Error("Missing required environment variable: EXTERNAL_API_URL");
}
if (
  missingRequiredExternalPublicUrl({
    stackPublicBaseDomain: config.stackPublicBaseDomain,
    selfHostedSingleTenant: config.selfHostedSingleTenant,
    externalPublicUrl: config.externalPublicUrl,
  })
) {
  throw new Error("Missing required environment variable: EXTERNAL_PUBLIC_URL");
}
